import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { SideUserPanel } from "@/components/layout/SideUserPanel";
import { BrandMark } from "@/components/BrandMark";
import { cn } from "@/lib/utils";
import {
  NAV_GROUPS, TOP_NAV, TOP_NAV_GROUPS,
  applyRoleFilter, applyPermissionFilter,
  activeTopNavId, firstLeafOf, visibleTopNavIds,
} from "@/components/layout/navConfig";

/** Vertical module list (the modules that used to be the top bar's buttons). */
export function SideNav({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }) {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const { canView } = usePermissions();

  const visibleGroups = useMemo(
    () => applyPermissionFilter(applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }), canView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seesWholePortfolio, isOrgAdmin, canView]
  );
  const shown = new Set(visibleTopNavIds(visibleGroups));
  const items = TOP_NAV.filter((t) => shown.has(t.id));
  const activeId = activeTopNavId(visibleGroups, location.pathname, projectId);

  // ungrouped items stay in order; grouped items collect under their group heading at first appearance
  const rows = [];
  const seen = new Set();
  items.forEach((t) => {
    if (t.group) {
      if (seen.has(t.group)) return;
      seen.add(t.group);
      rows.push({ type: "group", meta: TOP_NAV_GROUPS.find((g) => g.id === t.group), members: items.filter((m) => m.group === t.group) });
    } else rows.push({ type: "item", item: t });
  });

  const go = (id, after) => {
    const to = firstLeafOf(visibleGroups, id, projectId);
    if (to) navigate(to);
    after?.();
  };

  const Btn = ({ t, indent, narrow, after }) => {
    const Icon = t.icon;
    const active = t.id === activeId;
    return (
      <button
        type="button"
        data-testid={`topbar-item-${t.id}`}
        data-tour={`nav-${t.id}`}
        title={t.label}
        onClick={() => go(t.id, after)}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors",
          narrow ? "justify-center px-0" : indent ? "pl-8 pr-3" : "px-3",
          active ? "bg-[#910029] text-[#ECF4F7] shadow-sm" : "text-[#ECF4F7]/70 hover:bg-[#454D5C] hover:text-[#ECF4F7]"
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!narrow && <span className="truncate">{t.label}</span>}
      </button>
    );
  };

  const list = (narrow, after) => (
    <nav data-testid="sidenav-modules" className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
      {rows.map((r) =>
        r.type === "item" ? (
          <Btn key={r.item.id} t={r.item} narrow={narrow} after={after} />
        ) : (
          <div key={r.meta.id} className="pt-2">
            {!narrow && <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B7A8D]">{r.meta.label}</p>}
            {narrow && <div className="mx-2 my-1 border-t border-[#4A5260]" />}
            {r.members.map((m) => <Btn key={m.id} t={m} narrow={narrow} indent={false} after={after} />)}
          </div>
        )
      )}
    </nav>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/60" onClick={onMobileClose} aria-hidden="true" />
          <aside className="relative flex h-full w-[260px] flex-col bg-[#393F4B] shadow-2xl">
            <div className="flex h-11 shrink-0 items-center justify-between border-b border-[#4A5260] px-4">
              <BrandMark tagline />
              <button data-testid="mobile-sidebar-close" onClick={onMobileClose} aria-label="Close navigation" className="rounded-md p-1 text-[#8B9BB8] hover:bg-[#454D5C] hover:text-[#ECF4F7]">
                <X className="h-4 w-4" />
              </button>
            </div>
            {list(false, onMobileClose)}
            <SideUserPanel collapsed={false} />
          </aside>
        </div>
      )}

      <aside
        data-testid="app-sidebar"
        data-tour="nav-sidebar"
        className={cn("hidden h-full shrink-0 flex-col border-r border-[#4A5260] bg-[#393F4B] transition-all duration-300 md:flex", collapsed ? "w-16" : "w-56")}
      >
        <div className={cn("flex h-11 shrink-0 items-center border-b border-[#4A5260]", collapsed ? "justify-center" : "px-4")}>
          <BrandMark tagline collapsed={collapsed} />
        </div>
        {list(collapsed)}
        <SideUserPanel collapsed={collapsed} />
        <div className="shrink-0 border-t border-[#4A5260] p-2">
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={onToggleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn("flex h-9 items-center gap-2 rounded-lg px-3 text-xs text-[#8B9BB8] hover:bg-[#454D5C] hover:text-[#ECF4F7]", collapsed ? "mx-auto w-9 justify-center px-0" : "w-full")}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
          </button>
        </div>
      </aside>
    </>
  );
}
